import { createContext, useCallback, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { message } from "antd";

const DEFAULT_DURATION = 4;

const formatToastContent = ({ message: title, description }) => {
    if (!description) {
        return title;
    }
    return (
        <div className="inline-block max-w-[min(100vw-2rem,28rem)] text-left">
            <div className="font-medium">{title}</div>
            <div className="mt-0.5 text-sm opacity-90">{description}</div>
        </div>
    );
};

const toMessageConfig = (config) => {
    const { message: title, description, duration } = config;
    return {
        content: formatToastContent({ message: title, description }),
        duration: duration ?? DEFAULT_DURATION,
    };
};

const ToastContext = createContext();

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [api, contextHolder] = message.useMessage();

    const success = useCallback((config) => api.success(toMessageConfig(config)), [api]);
    const error = useCallback((config) => api.error(toMessageConfig(config)), [api]);
    const info = useCallback((config) => api.info(toMessageConfig(config)), [api]);
    const warning = useCallback((config) => api.warning(toMessageConfig(config)), [api]);
    const show = useCallback(
        (type, config) => api.open({ type, ...toMessageConfig(config) }),
        [api],
    );

    const toastApi = useMemo(
        () => ({ success, error, info, warning, show, api }),
        [success, error, info, warning, show, api],
    );

    return (
        <ToastContext.Provider value={toastApi}>
            {contextHolder}
            {children}
        </ToastContext.Provider>
    );
};

ToastProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
